import { useState } from 'react';
import type { ButtonHTMLAttributes, CSSProperties } from 'react';
import { Icon } from './Icon';
import type { IconName } from './Icon';

const SIZES = {
  sm: { box: 'var(--control-sm)', icon: 18 },
  md: { box: 'var(--control-md)', icon: 20 },
  lg: { box: 'var(--control-lg)', icon: 24 },
} as const;

const VARIANTS = {
  solid: {
    base: {
      background: 'var(--brand)',
      color: 'var(--brand-on)',
      border: '1px solid transparent',
      boxShadow: 'var(--shadow-sm)',
    },
    hover: { background: 'var(--brand-hover)' },
  },
  soft: {
    base: { background: 'var(--brand-subtle)', color: 'var(--brand)', border: '1px solid transparent' },
    hover: { filter: 'brightness(0.97)' },
  },
  outline: {
    base: { background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border-strong)' },
    hover: { background: 'var(--surface-sunken)' },
  },
  ghost: {
    base: { background: 'transparent', color: 'var(--text-muted)', border: '1px solid transparent' },
    hover: { background: 'var(--surface-sunken)' },
  },
} as const satisfies Record<string, { base: CSSProperties; hover: CSSProperties }>;

export type IconButtonProps = {
  icon: IconName | string;
  /** Always pass a label — it is the button's accessible name. */
  label: string;
  variant?: keyof typeof VARIANTS;
  size?: keyof typeof SIZES;
  round?: boolean;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label' | 'title'>;

/** IconButton — a square (or round), icon-only tappable control. */
export function IconButton({
  icon,
  label,
  variant = 'ghost',
  size = 'md',
  disabled = false,
  round = false,
  style,
  ...rest
}: IconButtonProps) {
  const s = SIZES[size] ?? SIZES.md;
  const v = VARIANTS[variant] ?? VARIANTS.ghost;
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);

  const styles: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: s.box,
    height: s.box,
    flexShrink: 0,
    borderRadius: round ? 'var(--radius-pill)' : 'var(--radius-md)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition:
      'background var(--duration-fast) var(--ease-standard), transform var(--duration-fast) var(--ease-soft), filter var(--duration-fast) var(--ease-standard)',
    transform: active && !disabled ? 'scale(0.94)' : 'scale(1)',
    ...v.base,
    ...(hover && !disabled ? v.hover : null),
    ...style,
  };

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      style={styles}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false);
        setActive(false);
      }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      {...rest}
    >
      <Icon name={icon} size={s.icon} />
    </button>
  );
}
